import { Checkbox, FormControlLabel } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useQuestionPointsOptions from '../../../../hooks/api/questions/useQuestionPointsOptions';
import { QuestionType, QuestionTypes } from '../../../../types/GlobalTypes';
import getInputState from '../../../../utils/getInputState/getInputState';
import { LabeledStateAutocompleteDropdown } from '../../../common/dropdowns/LabeledStateAutocompleteDropdown';
import { LabeledStateAutocompleteMultiDropdown } from '../../../common/dropdowns/LabeledStateAutocompleteMultiDropdown';
import FormSectionTitle from '../../../common/formSectionTitle/FormSectionTitle';
import { LabeledComponent } from '../../../common/inputs/LabeledComponent';
import { QuestionFormInputs, QuestionFormInputsKeys } from '../types/QuestionFormTypes';

interface QuestionFormPointsProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getErrorMessage: (field: QuestionFormInputsKeys) => any;
  questionType: QuestionType;
}

const QuestionFormPoints = ({ getErrorMessage, questionType }: QuestionFormPointsProps) => {
  const { t } = useTranslation();

  const { pointsOptions } = useQuestionPointsOptions();

  const { control, watch, setValue } = useFormContext<QuestionFormInputs>();

  const [pointOptionTextValue, setPointOptionTextValue] = useState('');

  const defaultPointsOptions =
    pointsOptions?.map(option => ({
      id: option.id.toString(),
      value: option.points,
    })) ?? [];

  const isAnimatorQuestion = questionType === QuestionTypes.ANIMATOR;
  const isMediaQuestion = questionType === QuestionTypes.VIDEO || questionType === QuestionTypes.IMAGE;

  const maxPointsOption = defaultPointsOptions.reduce((max, option) => Math.max(max, Number(option.value) || 0), 0);

  const points = watch('points');
  const pointsEditableDuringEvaluation = watch('pointsEditableDuringEvaluation');

  // Media (video/image) points default to the maximum available value, set once
  // when the options load and the field is still empty (so it never snaps back
  // while the admin is editing the value).
  const didDefaultRef = useRef(false);
  useEffect(() => {
    if (!didDefaultRef.current && isMediaQuestion && (points === undefined || points === null) && maxPointsOption > 0) {
      setValue('points', maxPointsOption);
      didDefaultRef.current = true;
    }
  }, [isMediaQuestion, points, maxPointsOption, setValue]);

  return (
    <>
      <FormSectionTitle className="mb-6 mt-10" title={t('questionForm.points.title')} />

      <div className="pb-7 max-w-lg">
        {isAnimatorQuestion && (
          <Controller
            name="multiplePointsOptions"
            control={control}
            render={({ field }) => (
              <LabeledStateAutocompleteMultiDropdown
                id="question-multiple-points-options"
                state={getInputState(getErrorMessage('multiplePointsOptions'))}
                labeledProps={{
                  label: t('questionForm.points.multiplePointsOptionsLabel'),
                  wrapperClassName: 'w-full',
                  errorMessage: t(`errorMessages.${getErrorMessage('multiplePointsOptions')?.message}`),
                }}
                dropdownProps={{
                  placeholder: t('questionForm.points.multiplePointsOptionsPlaceholder'),
                  query: pointOptionTextValue,
                  setQuery: setPointOptionTextValue,
                  allOptions: defaultPointsOptions ?? [],
                  setSelectedOptions: newVal => {
                    field.onChange(newVal);
                  },
                  selectedOptions: field.value ?? [],
                  allowAddNewOption: true,
                }}
              />
            )}
          />
        )}

        {isMediaQuestion && (
          <div className="flex flex-col gap-3">
            <LabeledComponent label={t('questionForm.points.pointsLabel')}>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={0}
                  max={maxPointsOption || 100}
                  step={1}
                  value={Number(points) || 0}
                  onChange={e => setValue('points', Number(e.target.value), { shouldDirty: true })}
                  aria-label={t('questionForm.points.pointsLabel')}
                  className="flex-1"
                />
                <input
                  type="number"
                  min={0}
                  value={points ?? ''}
                  onChange={e =>
                    setValue('points', e.target.value === '' ? undefined : Number(e.target.value), {
                      shouldDirty: true,
                    })
                  }
                  className="w-20 border border-grey300 rounded-md px-2 py-1 text-right"
                />
              </div>
            </LabeledComponent>

            {!!getErrorMessage('points')?.message && (
              <p className="text-error text-xs">{t(`errorMessages.${getErrorMessage('points')?.message}`)}</p>
            )}

            <div>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!pointsEditableDuringEvaluation}
                    onChange={e => setValue('pointsEditableDuringEvaluation', e.target.checked, { shouldDirty: true })}
                  />
                }
                label={t('questionForm.points.editableDuringEvaluationLabel')}
              />
              <p className="text-xs text-grey500 ml-3">{t('questionForm.points.editableDuringEvaluationHint')}</p>
            </div>
          </div>
        )}

        {!isAnimatorQuestion && !isMediaQuestion && (
          <Controller
            name="points"
            control={control}
            render={({ field }) => (
              <LabeledStateAutocompleteDropdown
                id="question-points"
                state={getInputState(getErrorMessage('points'))}
                labeledProps={{
                  label: t('questionForm.points.pointsLabel'),
                  wrapperClassName: 'w-full',
                  errorMessage: t(`errorMessages.${getErrorMessage('points')?.message}`),
                }}
                dropdownProps={{
                  placeholder: t('questionForm.points.pointsPlaceholder'),
                  allOptions: defaultPointsOptions,
                  autocompleteValue: field.value?.toString() ?? '',
                  setAutocompleteValue: (newVal: string) => {
                    if (newVal === '') {
                      field.onChange(null);
                      return;
                    }

                    const isNumber = /^\d+(\.\d+)?$/.test(newVal);

                    if (!isNumber) {
                      return;
                    }

                    field.onChange(+newVal);
                  },
                }}
              />
            )}
          />
        )}
      </div>
    </>
  );
};

export default QuestionFormPoints;
